import React, { useState } from 'react';
import { Button, ColorPicker, Empty, Flex, Input, Modal, Popconfirm, Tooltip, Typography, message } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import * as actions from 'store/actions/AuthActions';
import { LEAD_TAG_COLORS, LeadTagChip, nextLeadTagColor } from './leadTags';

const { Text } = Typography;

const toHex = (color) => (typeof color === 'string' ? color : color?.toHexString?.());

const apiError = (e, fallback) => {
  const data = e?.response?.data;
  return data?.name?.[0] || data?.color?.[0] || data?.ids?.[0] || fallback;
};

const TagColorPicker = (props) => (
  <ColorPicker
    size="small"
    format="hex"
    disabledAlpha
    presets={[{ label: 'Suggested', colors: LEAD_TAG_COLORS }]}
    {...props}
  />
);

const SortableTagRow = ({ tag, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tag.name);
  const [color, setColor] = useState(tag.color);
  const [saving, setSaving] = useState(false);

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: tag.id, disabled: editing });

  const startEditing = () => {
    setName(tag.name);
    setColor(tag.color);
    setEditing(true);
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const ok = await onSave(tag, { name: name.trim(), color });
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <Flex
      ref={setNodeRef}
      align="center"
      gap={8}
      style={{
        padding: '6px 8px',
        border: '1px solid #f0f0f0',
        borderRadius: 6,
        marginBottom: 6,
        background: isDragging ? '#fafafa' : '#fff',
        boxShadow: isDragging ? '0 2px 8px rgba(0,0,0,0.12)' : undefined,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <span
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        style={{ cursor: editing ? 'not-allowed' : 'grab', color: '#8c8c8c', touchAction: 'none', display: 'flex' }}
      >
        <HolderOutlined />
      </span>
      {editing ? (
        <>
          <TagColorPicker value={color} onChange={(value) => setColor(toHex(value))} />
          <Input
            size="small"
            autoFocus
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            onPressEnter={save}
            onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
            style={{ flex: 1 }}
          />
          <Tooltip title="Save">
            <Button size="small" type="primary" icon={<CheckOutlined />} loading={saving} onClick={save} />
          </Tooltip>
          <Tooltip title="Cancel">
            <Button size="small" icon={<CloseOutlined />} onClick={() => setEditing(false)} />
          </Tooltip>
        </>
      ) : (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <LeadTagChip tag={tag} />
          </div>
          <Tooltip title="Edit">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={startEditing} />
          </Tooltip>
          <Popconfirm
            title={`Delete "${tag.name}"?`}
            description="It will be removed from every lead that has it."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(tag)}
          >
            <Tooltip title="Delete">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </>
      )}
    </Flex>
  );
};

/**
 * The lead tag list on its own: drag to reorder, add, rename / recolour and
 * delete. Every change is saved as it is made. `tags` is the ordered list the
 * Leads page holds; `onTagsChange` is its state setter (called with a new
 * list or an updater function).
 */
const ManageLeadTagsModal = ({ open, onClose, tags = [], onTagsChange }) => {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(null);
  const [adding, setAdding] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const color = newColor || nextLeadTagColor(tags);

  const addTag = async () => {
    const name = newName.trim();
    if (!name || adding) return;
    setAdding(true);
    try {
      const tag = await actions.createLeadTag({ name, color });
      onTagsChange((list) => [...list, tag]);
      setNewName('');
      setNewColor(null);
    } catch (e) {
      message.error(apiError(e, 'Could not add the tag'));
    }
    setAdding(false);
  };

  const saveTag = async (tag, changes) => {
    try {
      const saved = await actions.updateLeadTag(tag.id, changes);
      onTagsChange((list) => list.map((t) => (t.id === saved.id ? saved : t)));
      return true;
    } catch (e) {
      message.error(apiError(e, 'Could not save the tag'));
      return false;
    }
  };

  const deleteTag = async (tag) => {
    try {
      await actions.deleteLeadTag(tag.id);
      onTagsChange((list) => list.filter((t) => t.id !== tag.id));
      message.success(`Deleted "${tag.name}"`);
    } catch (e) {
      message.error(apiError(e, 'Could not delete the tag'));
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const previous = tags;
    const moved = arrayMove(
      tags,
      tags.findIndex((t) => t.id === active.id),
      tags.findIndex((t) => t.id === over.id)
    ).map((tag, position) => ({ ...tag, position }));
    // Show the new order straight away; put it back if the save fails.
    onTagsChange(moved);
    try {
      onTagsChange(await actions.reorderLeadTags(moved.map((t) => t.id)));
    } catch (e) {
      onTagsChange(previous);
      message.error(apiError(e, 'Could not save the new order'));
    }
  };

  return (
    <Modal
      title="Lead Tags"
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Done</Button>}
      width={460}
      destroyOnHidden
    >
      <Flex gap={8} align="center" style={{ marginBottom: 12 }}>
        <TagColorPicker value={color} onChange={(value) => setNewColor(toHex(value))} />
        <Input
          placeholder="New tag name"
          value={newName}
          maxLength={100}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={addTag}
        />
        <Button type="primary" icon={<PlusOutlined />} loading={adding} disabled={!newName.trim()} onClick={addTag}>
          Add
        </Button>
      </Flex>
      {tags.length > 0 && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          Drag to change the order tags are shown in.
        </Text>
      )}
      <div style={{ maxHeight: '55vh', overflowY: 'auto', overflowX: 'hidden' }}>
        {tags.length ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={tags.map((t) => t.id)} strategy={verticalListSortingStrategy}>
              {tags.map((tag) => (
                <SortableTagRow key={tag.id} tag={tag} onSave={saveTag} onDelete={deleteTag} />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tags yet - add one above" />
        )}
      </div>
    </Modal>
  );
};

export default ManageLeadTagsModal;
